import type { Task, FullProject } from '../../types';
import {
  areProjectIdsMatching,
  isCommProject,
  isSystemProject,
  filterAndSortRealProjects,
} from '../../utils/idNormalizer';
import {
  buildPersons,
  extractNormalizedPersonCandidates,
  isLegacyActiveContact,
  isPersonRecord,
  normalizePersonName,
  resolveStakeholderSeed,
  type CommTask,
  type PersonEntry,
  type SyntheticPersonSeed,
} from './personModel';
import {
  buildSidebarPeopleSummaryMap,
  type SidebarPersonSummaryInfo,
} from './personDeduplicationBuilder';

export interface ExtractProjectPersonsParams {
  allProjects: FullProject[];
  allTasksGlobal?: Task[];
  activeTasks?: Task[];
  projectId?: string;
  viewMode?: string;
  activeWorkspace?: string;
  todayISO?: string;
}

export type SidebarPersonInfo = SidebarPersonSummaryInfo;

export interface ProjectPeopleResult {
  persons: PersonEntry[];
  personByKey: Map<string, PersonEntry>;
  sidebarPeople: string[];
  sidebarPeopleData: Map<string, SidebarPersonInfo>;
}

interface CachedProjectPeopleEntry {
  allTasksGlobalRef: Task[];
  activeTasksRef: Task[];
  result: ProjectPeopleResult;
}

const projectPersonsWeakCache = new WeakMap<FullProject[], Map<string, CachedProjectPeopleEntry>>();

/**
 * Extracts and computes the canonical person list for a project or global workspace view.
 * Guarantees 1:1 consistency between the Contact Kanban "Kontakte" pool and the PeopleSidebar.
 */
export function extractProjectPersons({
  allProjects,
  allTasksGlobal = [],
  activeTasks = [],
  projectId,
  viewMode: _viewMode,
  activeWorkspace = 'professional',
  todayISO = new Date().toISOString().split('T')[0]
}: ExtractProjectPersonsParams): ProjectPeopleResult {
  const cacheKey = `${projectId || 'global'}|${activeWorkspace}|${todayISO}`;
  let byScope = projectPersonsWeakCache.get(allProjects);
  if (byScope) {
    const cached = byScope.get(cacheKey);
    const activeTasksMatch =
      cached &&
      (cached.activeTasksRef === activeTasks ||
        (cached.activeTasksRef.length === 0 && activeTasks.length === 0));
    const allTasksMatch =
      cached &&
      (cached.allTasksGlobalRef === allTasksGlobal ||
        (cached.allTasksGlobalRef.length === 0 && allTasksGlobal.length === 0));

    if (cached && activeTasksMatch && allTasksMatch) {
      return cached.result;
    }
  }

  const isSpecific = Boolean(projectId && projectId !== 'global' && !isCommProject(projectId));
  const activeProj = isSpecific ? allProjects.find(p => areProjectIdsMatching(p.id, projectId)) : undefined;

  // Filter projects by workspace
  const workspaceProjects = allProjects.filter(p => {
    if (isCommProject(p.id)) return true;
    return !p.workspace || p.workspace === activeWorkspace;
  });

  const activeRealProjects = filterAndSortRealProjects(workspaceProjects, { activeWorkspace });
  const primaryRealProject = activeRealProjects[0];

  const resolveActiveRealPid = (pid?: string): string | null => {
    if (!pid || isSystemProject(pid)) return null;
    const matched = activeRealProjects.find(rp => areProjectIdsMatching(rp.id, pid));
    return matched ? matched.id : null;
  };

  const projectByPersonName = new Map<string, Set<string>>();
  const projectByContactId = new Map<string, Set<string>>();
  const registerMatch = (map: Map<string, Set<string>>, key: string, pid: string) => {
    if (!key || !pid) return;
    let set = map.get(key);
    if (!set) {
      set = new Set();
      map.set(key, set);
    }
    set.add(pid);
  };

  activeRealProjects.forEach(proj => {
    (proj.metadata?.stakeholders || []).forEach(s => {
      extractNormalizedPersonCandidates(s?.name).forEach(n => {
        registerMatch(projectByPersonName, n, proj.id);
      });
    });
    (proj.tasks || []).forEach(t => {
      if (t.archived) return;
      if (t.contactId) registerMatch(projectByContactId, String(t.contactId), proj.id);
      if (isPersonRecord(t)) {
        registerMatch(projectByContactId, String(t.id), proj.id);
        extractNormalizedPersonCandidates(t.name || t.assignee).forEach(n => {
          registerMatch(projectByPersonName, n, proj.id);
        });
      }
      if (t.assignee) {
        t.assignee.split(/[,;/]+/).forEach(a => {
          extractNormalizedPersonCandidates(a).forEach(n => {
            registerMatch(projectByPersonName, n, proj.id);
          });
        });
      }
    });
  });

  const inferRealProjectIdsForCommTask = (t: Task): string[] => {
    const matched = new Set<string>();
    const direct = resolveActiveRealPid(t.projectId);
    if (direct) matched.add(direct);
    if (Array.isArray(t.projectIds)) {
      t.projectIds.forEach(pid => {
        const valid = resolveActiveRealPid(pid);
        if (valid) matched.add(valid);
      });
    }
    if (t.contactId && projectByContactId.has(String(t.contactId))) {
      projectByContactId.get(String(t.contactId))!.forEach(pid => matched.add(pid));
    }
    if (projectByContactId.has(String(t.id))) {
      projectByContactId.get(String(t.id))!.forEach(pid => matched.add(pid));
    }
    const rawName = isPersonRecord(t) ? t.name || t.assignee : t.assignee || t.name;
    extractNormalizedPersonCandidates(rawName).forEach(norm => {
      if (projectByPersonName.has(norm)) {
        projectByPersonName.get(norm)!.forEach(pid => matched.add(pid));
      }
    });
    if (matched.size === 0 && primaryRealProject) {
      matched.add(primaryRealProject.id);
    }
    return Array.from(matched);
  };

  // Pre-pass over comm_internal person records
  workspaceProjects.forEach(proj => {
    if (proj.archived || !isCommProject(proj.id)) return;
    (proj.tasks || []).forEach(t => {
      if (t.archived || !isPersonRecord(t)) return;
      if ((t.workspace || 'professional') !== activeWorkspace) return;
      const realIds = inferRealProjectIdsForCommTask(t);
      const candidates = extractNormalizedPersonCandidates(t.name || t.assignee);
      realIds.forEach(pid => {
        registerMatch(projectByContactId, String(t.id), pid);
        candidates.forEach(n => registerMatch(projectByPersonName, n, pid));
      });
    });
  });

  // Collect all project tasks
  const projectTasksMap = new Map<string | number, Task>();
  if (isSpecific) {
    (activeProj?.tasks || []).forEach(t => projectTasksMap.set(t.id, t));
    activeTasks.forEach(t => projectTasksMap.set(t.id, t));
    allTasksGlobal.forEach(t => {
      if (t.projectId && areProjectIdsMatching(t.projectId, projectId)) {
        projectTasksMap.set(t.id, t);
      }
    });
  }

  const projectTasks = Array.from(projectTasksMap.values());

  // 1. Gather comm tasks
  const seenCommTaskIds = new Set<string>();
  const allCommTasks: CommTask[] = [];
  workspaceProjects.forEach(proj => {
    if (proj.archived) return;
    const isCurrentCommProject = isCommProject(proj.id);
    (proj.tasks || []).forEach(t => {
      if (isCurrentCommProject && (t.workspace || 'professional') !== activeWorkspace) return;
      const isPerson = isPersonRecord(t);
      const isComm = isCurrentCommProject || t.category === 'Kommunikation' || isPerson || isLegacyActiveContact(t);
      if (isComm && t.type !== 'milestone' && t.type !== 'deadline') {
        const key = `${proj.id}_${t.id}`;
        if (seenCommTaskIds.has(key)) return;
        seenCommTaskIds.add(key);

        if (isCurrentCommProject) {
          const inferredRealPids = inferRealProjectIdsForCommTask(t);
          const effectiveProjectId = isPerson ? 'comm_internal' : (inferredRealPids[0] || proj.id);
          const resolvedRealProjects = inferredRealPids
            .map(pid => activeRealProjects.find(wp => areProjectIdsMatching(wp.id, pid)))
            .filter((wp): wp is FullProject => Boolean(wp));
          const primaryResolved = resolvedRealProjects[0];

          allCommTasks.push({
            ...t,
            projectId: effectiveProjectId,
            projectIds: inferredRealPids.length > 0 ? inferredRealPids : t.projectIds,
            globalId: `${proj.id}_${t.id}`,
            projectColor: primaryResolved?.color || proj.color || 'bg-slate-500',
            projectName: resolvedRealProjects.length > 0
              ? (isPerson ? resolvedRealProjects.map(p => p.name).join(', ') : primaryResolved.name)
              : 'Allgemein'
          });
        } else {
          const extraRealPids = Array.isArray(t.projectIds)
            ? t.projectIds.map(pid => resolveActiveRealPid(pid)).filter((pid): pid is string => Boolean(pid))
            : [];
          const mergedPids = Array.from(new Set([proj.id, ...extraRealPids]));
          allCommTasks.push({
            ...t,
            projectId: proj.id,
            projectIds: mergedPids,
            globalId: `${proj.id}_${t.id}`,
            projectColor: proj.color || 'bg-slate-500',
            projectName: proj.name
          });
        }
      }
    });
  });

  // Also include any activeTasks or allTasksGlobal that are contacts/vorgaenge for activeProj
  if (isSpecific) {
    projectTasks.forEach(t => {
      if ((isPersonRecord(t) || t.category === 'Kommunikation') && !allCommTasks.some(ct => String(ct.id) === String(t.id))) {
        allCommTasks.push({
          ...t,
          projectId: projectId!,
          projectIds: Array.from(new Set([projectId!, ...(t.projectIds || [])])),
          globalId: `${projectId}_${t.id}`,
          projectName: activeProj?.name || ''
        });
      }
    });
  }

  // Project assignees & contactIds for scoping
  const projectTaskAssignees = new Set<string>();
  const projectContactIds = new Set<string>();

  if (isSpecific) {
    const addTaskParticipant = (t: Task) => {
      if (t.archived) return;
      if (t.contactId) projectContactIds.add(String(t.contactId));
      if (isPersonRecord(t)) {
        projectContactIds.add(String(t.id));
        extractNormalizedPersonCandidates(t.name || t.assignee).forEach(normRec => {
          projectTaskAssignees.add(normRec);
        });
      }
      if (t.assignee) {
        t.assignee.split(/[,;/]+/).forEach(a => {
          extractNormalizedPersonCandidates(a).forEach(clean => {
            projectTaskAssignees.add(clean);
          });
        });
      }
    };

    projectTasks.forEach(addTaskParticipant);
    allCommTasks.forEach(ct => {
      if (
        (ct.projectId && areProjectIdsMatching(ct.projectId, projectId)) ||
        (Array.isArray(ct.projectIds) && ct.projectIds.some(pid => areProjectIdsMatching(pid, projectId)))
      ) {
        addTaskParticipant(ct);
      }
    });
    (activeProj?.metadata?.stakeholders || []).forEach(s => {
      extractNormalizedPersonCandidates(s?.name).forEach(clean => {
        projectTaskAssignees.add(clean);
      });
    });
  }

  const doesPersonBelongToProject = (t: Task) => {
    if (!isSpecific || !projectId) return true;
    if (t.projectId && areProjectIdsMatching(t.projectId, projectId)) return true;
    if (Array.isArray(t.projectIds) && t.projectIds.some(pid => areProjectIdsMatching(pid, projectId))) return true;
    if (t.contactId && projectContactIds.has(String(t.contactId))) return true;
    if (projectContactIds.has(String(t.id))) return true;
    const candidates = extractNormalizedPersonCandidates(t.name || t.assignee);
    if (candidates.some(normName => projectTaskAssignees.has(normName))) return true;
    return false;
  };

  // Filter records
  const records = allCommTasks.filter(t => {
    if (!isPersonRecord(t) || t.archived) return false;
    return doesPersonBelongToProject(t);
  });

  const recordIdSet = new Set(records.map(r => String(r.id)));
  const recordNamesSet = new Set(
    records.map(r => normalizePersonName(r.name || r.assignee)).filter(Boolean)
  );

  const vorgaenge = allCommTasks.filter(t => {
    if (isPersonRecord(t) && !isLegacyActiveContact(t)) return false;
    if (!isSpecific || !projectId) return true;
    if (t.projectId && areProjectIdsMatching(t.projectId, projectId)) return true;
    if (Array.isArray(t.projectIds) && t.projectIds.some(pid => areProjectIdsMatching(pid, projectId))) return true;
    const hasOtherRealProject = Boolean(
      (resolveActiveRealPid(t.projectId) && !areProjectIdsMatching(t.projectId, projectId)) ||
      (Array.isArray(t.projectIds) && t.projectIds.some(pid => Boolean(resolveActiveRealPid(pid)) && !areProjectIdsMatching(pid, projectId)))
    );
    if (!hasOtherRealProject) {
      if (t.contactId && (recordIdSet.has(String(t.contactId)) || projectContactIds.has(String(t.contactId)))) return true;
      const candidates = extractNormalizedPersonCandidates(t.assignee || t.name);
      if (candidates.some(normName => recordNamesSet.has(normName) || projectTaskAssignees.has(normName))) return true;
    }
    return false;
  });

  const seeds: SyntheticPersonSeed[] = [];

  workspaceProjects.forEach(proj => {
    if (proj.archived || isSystemProject(proj.id, proj.name)) return;
    if (isSpecific && !areProjectIdsMatching(proj.id, projectId)) return;
    (proj.tasks || []).forEach(t => {
      if (t.archived || !t.assignee || !t.assignee.trim()) return;
      if (t.type === 'contact') return;
      if (t.contactId && recordIdSet.has(String(t.contactId))) return;
      t.assignee.split(/[,;/]+/).map(a => a.trim()).filter(a => a && a !== 'all').forEach(aName => {
        const norm = normalizePersonName(aName);
        if (norm) recordNamesSet.add(norm);
        seeds.push({ name: aName, projectId: proj.id, projectName: proj.name, role: t.role, organization: t.organization });
      });
    });
    (proj.metadata?.stakeholders || []).forEach((s: any) => {
      const resolvedSeed = resolveStakeholderSeed(s, proj.id, proj.name, recordNamesSet);
      if (resolvedSeed) {
        seeds.push(resolvedSeed);
      }
    });
  });

  const { persons } = buildPersons(records, seeds, vorgaenge);
  const scopedPersons = isSpecific
    ? persons.filter(p =>
        p.projectIds.some(pid => areProjectIdsMatching(pid, projectId)) ||
        p.records.some(r => doesPersonBelongToProject(r)) ||
        p.openVorgaenge.some(v => (v.projectId && areProjectIdsMatching(v.projectId, projectId)) || (v.projectIds || []).some(pid => areProjectIdsMatching(pid, projectId))) ||
        extractNormalizedPersonCandidates(p.name).some(normName => projectTaskAssignees.has(normName)) ||
        p.records.some(r => projectContactIds.has(String(r.id)))
      )
    : persons;

  const personByKey = new Map<string, PersonEntry>();
  scopedPersons.forEach(p => personByKey.set(p.key, p));

  const taskPool = isSpecific ? projectTasks : (allTasksGlobal.length > 0 ? allTasksGlobal : allCommTasks);
  const { sidebarPeople, sidebarPeopleData } = buildSidebarPeopleSummaryMap(
    scopedPersons,
    taskPool,
    todayISO
  );

  const result: ProjectPeopleResult = {
    persons: scopedPersons,
    personByKey,
    sidebarPeople,
    sidebarPeopleData
  };

  if (!byScope) {
    byScope = new Map();
    projectPersonsWeakCache.set(allProjects, byScope);
  }
  byScope.set(cacheKey, {
    allTasksGlobalRef: allTasksGlobal,
    activeTasksRef: activeTasks,
    result,
  });

  return result;
}
